<template>
  <v-card>
    <div
      id="blurHelper"
      tabindex="-1"
      style="position: absolute; left: -9999px; top: -9999px"
    ></div>
    <v-container>
      <v-row>
        <v-col cols="9" class="pb-0">
          <v-card-title id="title" tabindex="-1" class="text-button">{{
            isMobile
              ? "Nueva prescripción"
              : "Crear prescripción de medicamentos"
          }}</v-card-title>
        </v-col>
        <v-col cols="3" class="pt-0 pr-0">
          <v-card-actions class="pb-0">
            <v-spacer></v-spacer>
            <v-tooltip text="Cerrar" location="left">
              <template v-slot:activator="{ props }">
                <v-btn
                  color="primary"
                  v-bind="props"
                  :icon="closeIcon"
                  id="vBtnCloseCreateReceta"
                  tabindex="100"
                  @click="closeModal(false)"
                  @keydown.tab="focusFirstFieldOfCurrentStep($event)"
                >
                </v-btn>
              </template>
            </v-tooltip>
          </v-card-actions>
        </v-col>
      </v-row>
      <v-card-text class="py-0">
        <v-stepper v-model="step" :items="stepsComputed" hide-actions>
          <template v-slot:[`item.1`]>
            <v-card flat>
              <v-form v-model="isPacienteFormValid" ref="paciente-form"
                ><v-container :class="isMobile ? 'px-0 mx-0' : 'my-1'">
                  <v-row :no-gutters="isMobile">
                    <v-col :cols="isMobile ? 12 : 4" class="py-0">
                      <v-select
                        :items="financiadoresItems"
                        label="Financiador"
                        v-model="financiadorSelected"
                        autocomplete="off"
                        :tabindex="step === 1 ? 1 : -1"
                        @keydown.tab="focusLastFieldOfCurrentStep"
                        return-object
                        @update:model-value="setDataFinanciador()"
                        variant="outlined"
                        density="compact"
                        item-value="id"
                        item-title="value1"
                        id="FinanciadorSelect"
                        :rules="rules.required"
                      ></v-select>
                    </v-col>

                    <!-- Alerta de bloqueo de prescripción -->
                    <v-col
                      cols="12"
                      class="py-2"
                      v-if="
                        prestadorValidation && prestadorValidation.isBlocked
                      "
                    >
                      <v-alert
                        type="error"
                        variant="tonal"
                        prominent
                        border="start"
                        title="Prescripción bloqueada"
                        :text="
                          prestadorValidation.blockReason ||
                          'No se puede continuar con la prescripción debido a problemas de validación.'
                        "
                        class="mb-4"
                      >
                        <template v-slot:append>
                          <v-icon>mdi-lock</v-icon>
                        </template>
                      </v-alert>
                      <v-alert
                        v-if="financiadoresItems.length > 1"
                        type="info"
                        variant="tonal"
                        density="compact"
                        text="Seleccione otro financiador para reintentar la validación."
                      ></v-alert>
                    </v-col>

                    <!-- Resto del formulario - solo visible si no está bloqueado -->
                    <template
                      v-if="
                        !prestadorValidation || !prestadorValidation.isBlocked
                      "
                    >
                      <v-col :cols="isMobile ? 12 : 3" class="py-0">
                        <DatePicker
                          @selectFecha="selectFechaEmision($event)"
                          label="Fecha prescripción"
                          :minDate="minDateFechaEmisionFormatted"
                          :maxDate="maxDateFechaEmisionFormatted"
                          :disabled="false"
                          :tabindex="step === 1 ? 2 : -1"
                          :dateMenu="menuFechaEmision"
                          :defaultDate="fechaEmision"
                          :rules="[
                            ...rules.required,
                            ...rules.validDateFormat,
                            financiadorSelected != null
                              ? rules.dateSelectedInRange(
                                  fechaEmision,
                                  minDateFechaEmision,
                                  maxDateFechaEmision
                                )
                              : true,
                            rules.validDateRange(
                              selectedFechaNacimiento,
                              selectedFechaEmision
                            ),
                            rules.validDateRange(
                              formatDate(new Date()),
                              selectedFechaEmision
                            ),
                          ]"
                        />
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 5"
                        class="py-0"
                        v-if="
                          validaAfiliado != null &&
                          validaAfiliado &&
                          financiadorSelected != null
                        "
                      >
                        <v-autocomplete
                          label="Afiliado"
                          v-model="selectedAfiliado"
                          :items="afiliados"
                          autocomplete="off"
                          v-model:search="inputAfiliado"
                          ref="afiliados-autocomplete"
                          id="AfiliadoAutocomplete"
                          :tabindex="step === 1 ? 3 : -1"
                          return-object
                          class="mr-2"
                          variant="outlined"
                          density="compact"
                          item-title="docId"
                          item-value="nroAfiliado"
                          :hint="
                            financiadorSelected != null &&
                            fechaEmision != null &&
                            selectedAfiliado == null
                              ? 'Búsqueda por CUIL, DNI o N° afiliado'
                              : selectedAfiliado != null
                              ? ''
                              : 'Seleccionar fecha y financiador para buscar afiliado'
                          "
                          persistent-hint
                          :loading="loadingAfiliados"
                          :custom-filter="customFilterAfiliado"
                          :readonly="
                            selectedFechaEmision == null ||
                            financiadorSelected == null
                          "
                          clearable
                          :rules="validaAfiliado ? rules.required : []"
                          :menu-props="{ closeOnContentClick: true }"
                        >
                          <template v-slot:item="{ item }">
                            <v-list-item @click="setAfiliado(item)">
                              <v-list-item-title>{{
                                item.raw.benNombreCompleto
                              }}</v-list-item-title>
                              <v-list-item-subtitle
                                >N° afiliado: {{ item.value }}
                              </v-list-item-subtitle>
                              <v-list-item-subtitle
                                >CUIL: {{ item.raw.cuil }}
                              </v-list-item-subtitle>
                              <v-list-item-subtitle
                                >DNI: {{ item.raw.docId }}
                              </v-list-item-subtitle>
                              <v-list-item-subtitle
                                v-if="item.raw.estadoBenef != null"
                                >Estado: {{ item.raw.estadoBenef.value }}
                              </v-list-item-subtitle>
                            </v-list-item>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 4"
                        class="py-0"
                        :class="isMobile ? 'pt-1' : ''"
                        v-if="
                          validaAfiliado != null &&
                          validaAfiliado &&
                          selectedAfiliado != null
                        "
                      >
                        <v-select
                          :items="planesItems"
                          label="Plan"
                          v-model="planSelected"
                          return-object
                          variant="outlined"
                          density="compact"
                          item-value="id"
                          :readonly="validaAfiliado"
                          item-title="value"
                          :tabindex="step === 1 ? 4 : -1"
                          :rules="rules.required"
                        ></v-select>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 10 : 3"
                        class="py-0"
                        :class="isMobile ? 'pt-1' : ''"
                        v-else-if="
                          validaAfiliado != null &&
                          !validaAfiliado &&
                          financiadorSelected != null &&
                          !afiliadoSearched &&
                          solicitudItem == null
                        "
                      >
                        <v-text-field
                          label="Buscar afiliado"
                          variant="outlined"
                          density="compact"
                          hint="Búsqueda por CUIL, DNI o N° afiliado"
                          persistent-hint
                          :tabindex="step === 1 ? 3 : -1"
                          :rules="rules.required"
                          v-model="searchAfiliadoField"
                          @keydown.enter="searchAfiliado()"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 2 : 1"
                        class="py-0"
                        :class="isMobile ? 'pt-2 pl-1 pr-0' : ''"
                        v-if="
                          validaAfiliado != null &&
                          !validaAfiliado &&
                          financiadorSelected != null &&
                          !afiliadoSearched &&
                          solicitudItem == null
                        "
                      >
                        <v-tooltip text="Buscar afiliado" location="left">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              color="primary"
                              :icon="searchIcon"
                              id="buscarAfiliadoBtn"
                              :tabindex="step === 1 || (searchAfiliadoField && searchAfiliadoField.trim().length > 1) ? 4 : -1"
                              v-bind="props"
                              :loading="isLoadingSearchAfiliado"
                              :disabled="searchAfiliadoField == null || searchAfiliadoField.trim().length < 1"
                              @click="searchAfiliado()"
                              :size="isMobile ? 'x-small' : 'small'"
                            >
                            </v-btn>
                          </template>
                        </v-tooltip>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 4"
                        class="py-0"
                        :class="isMobile ? 'pt-1' : ''"
                        v-if="
                          validaAfiliado != null &&
                          !validaAfiliado &&
                          financiadorSelected != null &&
                          afiliadoSearched
                        "
                      >
                        <v-text-field
                          label="Plan"
                          variant="outlined"
                          density="compact"
                          :tabindex="step === 1 ? 5 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(planSelected, 50)],
                              rules.nameRulesWithNumber
                            )
                          "
                          v-model="planSelected"
                        ></v-text-field>
                      </v-col>

                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          selectedAfiliado != null &&
                          validaAfiliado != null &&
                          validaAfiliado
                        "
                      >
                        <v-text-field
                          label="Apellido/s y nombre/s paciente"
                          variant="outlined"
                          density="compact"
                          :tabindex="step === 1 ? 5 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(pacienteApellidoNombre, 50)],
                              rules.nameRules
                            )
                          "
                          :model-value="pacienteApellidoNombre"
                          readonly
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : !validaAfiliado ? 4 : 3"
                        class="py-0"
                        v-if="
                          financiadorSelected != null &&
                          validaAfiliado != null &&
                          !validaAfiliado &&
                          afiliadoSearched
                        "
                      >
                        <v-text-field
                          label="Apellido/s"
                          variant="outlined"
                          density="compact"
                          :tabindex="step === 1 ? 6 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(pacienteApellido, 50)],
                              rules.nameRules
                            )
                          "
                          v-model="pacienteApellido"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          financiadorSelected != null &&
                          validaAfiliado != null &&
                          !validaAfiliado &&
                          afiliadoSearched
                        "
                      >
                        <v-text-field
                          label="Nombre/s"
                          variant="outlined"
                          density="compact"
                          :tabindex="step === 1 ? 7 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(pacienteNombre, 50)],
                              rules.nameRules
                            )
                          "
                          v-model="pacienteNombre"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 8 : 2"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-select
                          :items="tiposDocItems"
                          label="Tipo documento"
                          v-model="tipoDocSelected"
                          :tabindex="step === 1 ? 8 : -1"
                          return-object
                          variant="outlined"
                          density="compact"
                          item-value="id"
                          item-title="value1"
                          :rules="rules.required"
                          @update:model-value="handlePacienteDataChange"
                        ></v-select>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-text-field
                          v-if="shouldMaskDocumento"
                          label="Número documento"
                          density="compact"
                          variant="outlined"
                          id="textFieldDocumento"
                          :tabindex="step === 1 ? 9 : -1"
                          v-mask="['#.###.###', '##.###.###']"
                          :rules="rules.required"
                          v-model="pacienteDocumento"
                          @update:model-value="handlePacienteDataChange"
                        ></v-text-field>
                        <v-text-field
                          v-else
                          label="Número documento"
                          density="compact"
                          variant="outlined"
                          id="textFieldDocumento"
                          :tabindex="step === 1 ? 9 : -1"
                          :rules="
                            rules.required.concat([
                              rules.maxLength(pacienteDocumento, 20),
                            ])
                          "
                          v-model="pacienteDocumento"
                          @update:model-value="handlePacienteDataChange"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-select
                          :items="generosItems"
                          label="Sexo"
                          v-model="generoSelected"
                          return-object
                          :tabindex="step === 1 ? 10 : -1"
                          variant="outlined"
                          density="compact"
                          item-value="id"
                          item-title="value"
                          :rules="rules.required"
                          @update:model-value="handlePacienteDataChange"
                        ></v-select>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-text-field
                          label="CUIL"
                          density="compact"
                          variant="outlined"
                          :tabindex="step === 1 ? 11 : -1"
                          v-mask="['##-#######-#', '##-########-#']"
                          :rules="
                            isDocumentoDNI
                              ? rules.required.concat([rules.validateCuilCoincideConDni(pacienteCuil, pacienteDocumento, generoSelected?.id)])
                              : pacienteCuil
                              ? [rules.validateCUITCUIL(pacienteCuil)]
                              : []
                          "
                          v-model="pacienteCuil"
                        >
                          <template v-slot:append-inner v-if="showGenerateCuil">
                            <v-btn
                              color="primary"
                              variant="tonal"
                              rounded="pill"
                              density="comfortable"
                              size="small"
                              class="text-none"
                              :loading="generatingCuil"
                              @click.stop="generarCUIL"
                            >
                              Generar CUIL
                            </v-btn>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 2"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-text-field
                          label="Número afiliado"
                          density="compact"
                          variant="outlined"
                          id="textFieldNroAfiliado"
                          :tabindex="step === 1 ? 12 : -1"
                          :readonly="validaAfiliado"
                          v-mask="'##############'"
                          v-model="pacienteNumero"
                          :rules="rules.required"
                          @update:model-value="handlePacienteDataChange"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <DatePicker
                          @selectFecha="selectFechaNacimiento($event)"
                          label="Fecha nacimiento"
                          :minDate="minDateFechaNacimiento"
                          :maxDate="maxDateFechaNacimientoFormatted"
                          :disabled="false"
                          :tabindex="step === 1 ? 13 : -1"
                          :dateMenu="menuFechaNacimiento"
                          :defaultDate="fechaNacimiento"
                          :rules="[
                            ...rules.required,
                            ...rules.validDateFormat,
                            rules.validDateRange(
                              selectedFechaNacimiento,
                              formatDate(new Date())
                            ),
                          ]"
                        />
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 4"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-text-field
                          label="Email"
                          density="compact"
                          variant="outlined"
                          :tabindex="step === 1 ? 14 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(pacienteEmail, 100)],
                              rules.required,
                              rules.emailRules
                            )
                          "
                          v-model="pacienteEmail"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0 pr-0 mr-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-select
                          v-model="selectedPrefix"
                          :items="countryPrefixes"
                          label="Prefijo"
                          item-value="value"
                          item-title="text"
                          density="compact"
                          variant="outlined"
                          :tabindex="step === 1 ? 15 : -1"
                          class="mr-0 pr-0"
                        ></v-select>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 4"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-text-field
                          label="Teléfono"
                          density="compact"
                          variant="outlined"
                          :tabindex="step === 1 ? 16 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(pacienteTelefono, 50)],
                              rules.required,
                              rules.telephoneRules
                            )
                          "
                          :prefix="selectedPrefix"
                          v-model="pacienteTelefono"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        class="py-0"
                        :cols="isMobile ? 12 : 12"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-alert
                          type="info"
                          variant="tonal"
                          density="compact"
                          class="to-right"
                        >
                          Importante: verifique que los datos ingresados sean
                          correctos.
                        </v-alert>
                      </v-col>
                    </template>
                  </v-row>
                </v-container>
              </v-form>
            </v-card>
          </template>

          <template v-slot:[`item.2`]>
            <v-card flat
              ><v-form v-model="isPrescripcionFormValid" ref="prescripcion-form"
                ><v-container :class="isMobile ? 'px-0' : ''">
                  <v-row :no-gutters="isMobile">
                    <v-col :cols="isMobile ? 12 : 12" class="py-1">
                      <v-textarea
                        v-model.trim="diagnostico"
                        label="Diagnóstico"
                        variant="outlined"
                        id="diagnosticoAutocomplete"
                        autocomplete="off"
                        density="compact"
                        :tabindex="step === 2 ? 1 : -1"
                        @keydown.tab="focusLastFieldOfCurrentStep"
                        auto-grow
                        :rules="
                          rules.required.concat([
                            rules.maxLength(diagnostico, 200),
                          ])
                        "
                        rows="1"
                      ></v-textarea>
                    </v-col>

                    <!-- Selección de matrícula -->
                    <v-col :cols="isMobile ? 12 : 4" class="py-1">
                      <!-- Siempre visible -->
                      <template v-if="loadingMatriculas">
                        <v-text-field
                          label="Matrícula a utilizar"
                          variant="outlined"
                          density="compact"
                          readonly
                          :loading="true"
                          model-value="Buscando matrícula..."
                          tabindex="-1"
                        ></v-text-field>
                      </template>
                      <template v-else-if="matriculasDisponibles.length > 1">
                        <v-select
                          :items="matriculasDisponibles"
                          label="Matrícula a utilizar"
                          v-model="matriculaSeleccionada"
                          return-object
                          autocomplete="off"
                          variant="outlined"
                          density="compact"
                          item-title="display"
                          item-value="preXfinId"
                          :tabindex="step === 2 ? 3 : -1"
                          :rules="rules.required"
                          @update:model-value="onMatriculaChange"
                        ></v-select>
                      </template>
                      <template v-else-if="matriculasDisponibles.length === 1">
                        <v-text-field
                          label="Matrícula"
                          variant="outlined"
                          density="compact"
                          readonly
                          :model-value="matriculasDisponibles[0]?.display"
                          tabindex="-1"
                        ></v-text-field>
                      </template>
                      <!-- Si no hay matrículas después de cargar, mostrar vacío (asumiendo que no sucede) -->
                      <template v-else>
                        <v-text-field
                          label="Matrícula"
                          variant="outlined"
                          density="compact"
                          readonly
                          model-value="No disponible"
                          tabindex="-1"
                        ></v-text-field>
                      </template>
                    </v-col>
                    <v-col :cols="isMobile ? 12 : 8" class="py-1">
                      <v-textarea
                        v-model.trim="domicilioPrescripcion"
                        label="Domicilio prescripción"
                        variant="outlined"
                        id="domicilioAutocomplete"
                        autocomplete="street-address"
                        density="compact"
                        :tabindex="step === 2 ? 2 : -1"
                        auto-grow
                        :disabled="!matriculaSeleccionada && loadingMatriculas"
                        :rules="
                          rules.required.concat([
                            rules.maxLength(domicilioPrescripcion, 150),
                          ])
                        "
                        rows="1"
                      ></v-textarea>
                    </v-col>
                    <v-divider v-if="!isMobile"></v-divider>
                    <v-col :cols="isMobile ? 10 : 6" class="pb-0">
                      <v-select
                        label="Medicamento"
                        v-model="selectedMedicamento"
                        :items="medicamentos"
                        return-object
                        autocomplete="off"
                        class="mr-2"
                        variant="outlined"
                        density="compact"
                        item-title="nombrePresentacion"
                        item-value="id"
                        :tabindex="step === 2 ? 4 : -1"
                        clearable
                        readonly
                      >
                      </v-select>
                    </v-col>
                    <v-col :cols="isMobile ? 2 : 1" class="pb-0">
                      <v-tooltip text="Buscar medicamentos" location="left">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            color="primary"
                            :icon="searchIcon"
                            id="buscarMedBtn"
                            :tabindex="step === 2 ? 5 : -1"
                            v-bind="props"
                            @click="openModalMedicamentos()"
                            :size="isMobile ? 'x-small' : 'small'"
                          >
                          </v-btn>
                        </template>
                      </v-tooltip>
                    </v-col>
                    <v-col
                      :cols="isMobile ? 4 : 2"
                      class="pb-0"
                      :class="isMobile ? 'pt-1 pb-1' : ''"
                    >
                      <v-text-field
                        :label="isMobile ? 'Cantidad' : 'Cantidad'"
                        variant="outlined"
                        density="compact"
                        min="1"
                        class="center-input"
                        id="cantidadMed"
                        type="number"
                        :tabindex="step === 2 ? 6 : -1"
                        v-model="medicamentoCantidad"
                        :rules="rules.required.concat(rules.validNumberRule99)"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" class="py-0">
                      <v-textarea
                        v-model="posologia"
                        label="Posología"
                        variant="outlined"
                        density="compact"
                        :tabindex="step === 2 ? 7 : -1"
                        auto-grow
                        rows="1"
                      ></v-textarea>
                    </v-col>
                    <v-col cols="12" class="py-0">
                      <v-btn
                        density="compact"
                        color="primary"
                        class="to-right"
                        :tabindex="step === 2 ? 8 : -1"
                        @click="addMedicamento()"
                        :disabled="
                          selectedMedicamento == null ||
                          medicamentoCantidad <= 0
                        "
                        >Agregar</v-btn
                      >
                    </v-col>
                    <v-col cols="12" :class="isMobile ? 'pb-0 pt-3' : 'py-0'">
                      <v-data-table
                        :items="medicamentosAgregados"
                        item-value="id"
                        :mobile="isMobile"
                        :hide-default-header="isMobile"
                        :hide-no-data="isMobile"
                        density="compact"
                        :headers="headersMedicamentosAgregados"
                      >
                        <template v-slot:[`item.regNom`]="{ item }">
                          <span :style="isMobile ? 'font-size: 10px' : ''">
                            {{
                              (item.regNom ?? "").length > 65
                                ? item.regNom.substring(0, 62) + "..."
                                : item.regNom ?? ""
                            }}</span
                          ></template
                        >
                        <template v-slot:[`item.condicionExpendio`]="{ item }">
                          <CondicionExpendio
                            :codigo="item.condicionExpendioCodigo"
                            :nombre="item.condicionExpendio"
                            :advertencia="item.condicionExpendioAdvertencia"
                            :alignRight="isMobile"
                            :center="!isMobile"
                          />
                        </template>
                        <template v-slot:[`item.actions`]="{ item, index }">
                          <v-tooltip text="Editar medicamento" location="start">
                            <template v-slot:activator="{ props }">
                              <v-icon
                                size="20"
                                @click="editMedicamento(item)"
                                @keydown.enter="editMedicamento(item)"
                                v-bind="props"
                                :tabindex="step === 2 ? 20 + (index * 2) : -1"
                              >
                                {{ editIcon }}
                              </v-icon>
                            </template>
                          </v-tooltip>
                          <v-tooltip
                            text="Eliminar medicamento"
                            location="start"
                          >
                            <template v-slot:activator="{ props }">
                              <v-icon
                                size="20"
                                v-bind="props"
                                @click="deleteMedicamento(item)"
                                @keydown.enter="deleteMedicamento(item)"
                                :tabindex="step === 2 ? 21 + (index * 2) : -1"
                              >
                                {{ deleteIcon }}
                              </v-icon>
                            </template>
                          </v-tooltip>
                        </template>
                        <template #bottom></template>
                      </v-data-table>
                    </v-col>
                  </v-row> </v-container
              ></v-form>
            </v-card>
          </template>
          <!-- Step 3: Dinámico: Editar domicilio (si hay restringido) o Vista previa (si no) -->
          <template v-slot:[`item.3`]>
            <v-card flat class="editar-domicilio-step">
              <template v-if="hasMedicamentoRestringido()">
                <!-- Botones de acción -->
                <v-row class="mb-0 mr-2">
                  <v-col cols="12" class="d-flex justify-end">
                    <v-btn
                      id="importar-domicilio"
                      density="compact"
                      color="primary"
                      class="mr-2"
                      :tabindex="isEditingDomicilio ? 1 : -1"
                      @click="showConfirmarDomicilio = true"
                      @keydown.tab="focusLastFieldOfCurrentStep"
                    >
                      Buscar domicilio
                    </v-btn>
                    <v-btn
                      id="limpiar-domicilio"
                      density="compact"
                      color="primary"
                      :tabindex="isEditingDomicilio ? 2 : -1"
                      @click="limpiarCamposDomicilioConValidacion"
                    >
                      Limpiar
                    </v-btn>
                  </v-col>
                </v-row>
                <v-form v-model="isDomicilioFormValid" ref="domicilio-form">
                  <v-container :class="isMobile ? 'px-0' : ''">
                    <v-row>
                      <!-- Calle -->
                      <v-col :cols="isMobile ? 12 : 6" class="py-1">
                        <v-text-field
                          id="domicilio-calle"
                          label="Calle"
                          variant="outlined"
                          density="compact"
                          v-model="domicilioForm.Calle"
                          :clearable="true"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :rules="[
                            rules.maxLength(domicilioForm.Calle, 80)
                          ]"
                          :tabindex="isEditingDomicilio ? 3 : -1"
                        ></v-text-field>
                      </v-col>

                      <!-- Numero -->
                      <v-col :cols="isMobile ? 12 : 2" class="py-1">
                        <v-text-field
                          id="domicilio-numero"
                          label="Número"
                          variant="outlined"
                          density="compact"
                          v-model="domicilioForm.Numero"
                          :clearable="true"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :rules="[
                            rules.maxLength(domicilioForm.Numero, 6)
                          ]"
                          :tabindex="isEditingDomicilio ? 4 : -1"
                        ></v-text-field>
                      </v-col>

                      <!-- Piso y Departamento en la misma línea -->
                      <v-col :cols="isMobile ? 6 : 1.5" class="py-1">
                        <v-text-field
                          id="domicilio-piso"
                          label="Piso"
                          variant="outlined"
                          density="compact"
                          v-model="domicilioForm.Piso"
                          :clearable="true"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :rules="[
                            rules.maxLength(domicilioForm.Piso, 2)
                          ]"
                          :tabindex="isEditingDomicilio ? 5 : -1"
                        ></v-text-field>
                      </v-col>
                      <v-col :cols="isMobile ? 6 : 1.5" class="py-1">
                        <v-text-field
                          id="domicilio-departamento"
                          label="Departamento"
                          variant="outlined"
                          density="compact"
                          v-model="domicilioForm.Departamento"
                          :clearable="true"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :rules="[
                            rules.maxLength(domicilioForm.Departamento, 4)
                          ]"
                          :tabindex="isEditingDomicilio ? 6 : -1"
                        ></v-text-field>
                      </v-col>

                      <!-- Monoblock -->
                      <v-col :cols="isMobile ? 12 : 4" class="py-1">
                        <v-text-field
                          id="domicilio-monoblock"
                          label="Monoblock"
                          variant="outlined"
                          density="compact"
                          v-model="domicilioForm.Monoblock"
                          :clearable="true"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :rules="[
                            rules.maxLength(domicilioForm.Monoblock, 30)
                          ]"
                          :tabindex="isEditingDomicilio ? 7 : -1"
                        ></v-text-field>
                      </v-col>

                      <!-- Barrio -->
                      <v-col :cols="isMobile ? 12 : 4" class="py-1">
                        <v-text-field
                          id="domicilio-barrio"
                          label="Barrio"
                          variant="outlined"
                          density="compact"
                          v-model="domicilioForm.Barrio"
                          :clearable="true"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :rules="[
                            rules.maxLength(domicilioForm.Barrio, 30)
                          ]"
                          :tabindex="isEditingDomicilio ? 8 : -1"
                        ></v-text-field>
                      </v-col>

                      <!-- Ciudad -->
                      <v-col :cols="isMobile ? 12 : 4" class="py-1">
                        <v-text-field
                          id="domicilio-ciudad"
                          label="Ciudad"
                          variant="outlined"
                          density="compact"
                          v-model="domicilioForm.Ciudad"
                          :clearable="true"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :rules="[
                            rules.maxLength(domicilioForm.Ciudad, 60)
                          ]"
                          :tabindex="isEditingDomicilio ? 9 : -1"
                        ></v-text-field>
                      </v-col>
                      </v-row>

                      <!-- Nueva fila 2: Código Postal, Municipio, País, Provincia -->
                      <v-row>
                      <!-- Código Postal -->
                      <v-col :cols="isMobile ? 12 : 3" class="py-1">
                        <v-text-field
                          id="domicilio-cpostal"
                          label="Código Postal"
                          variant="outlined"
                          density="compact"
                          v-model="domicilioForm.CPostal"
                          :clearable="true"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :rules="[
                            rules.maxLength(domicilioForm.CPostal, 6)
                          ]"
                          :tabindex="isEditingDomicilio ? 10 : -1"
                        ></v-text-field>
                      </v-col>

                      <!-- Municipio -->
                      <v-col :cols="isMobile ? 12 : 3" class="py-1">
                        <v-text-field
                          id="domicilio-municipio"
                          label="Municipio / Departamento / Partido"
                          variant="outlined"
                          density="compact"
                          v-model="domicilioForm.Municipio"
                          :clearable="true"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :rules="[
                            rules.maxLength(domicilioForm.Municipio, 60)
                          ]"
                          :tabindex="isEditingDomicilio ? 11 : -1"
                        ></v-text-field>
                      </v-col>

                      <!-- País -->
                      <v-col :cols="isMobile ? 12 : 3" class="py-1">
                        <v-combobox
                          id="domicilio-pais"
                          label="País"
                          v-model="domicilioForm.Pais"
                          :items="paisesItems"
                          variant="outlined"
                          density="compact"
                          clearable
                          item-title="text"
                          item-value="value"
                          :rules="[
                            rules.maxLength(domicilioForm.Pais, 40)
                          ]"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :tabindex="isEditingDomicilio ? 12 : -1"
                        ></v-combobox>
                      </v-col>

                      <!-- Provincia -->
                      <v-col :cols="isMobile ? 12 : 3" class="py-1">
                        <v-combobox
                          id="domicilio-provincia"
                          label="Provincia"
                          v-model="provinciaValue"
                          :items="provinciasFiltradas"
                          variant="outlined"
                          density="compact"
                          clearable
                          item-title="value"
                          item-value="value"
                          :rules="[
                            rules.maxLength(this.domicilioForm.Provincia, 40)
                          ]"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :tabindex="isEditingDomicilio ? 13 : -1"
                        ></v-combobox>
                      </v-col>
                      </v-row>

                      <!-- Nueva fila para observaciones -->
                      <v-row>

                      <!-- Observaciones al final en su propia línea -->
                      <v-col cols="12" class="py-1">
                        <v-textarea
                          id="domicilio-observaciones"
                          v-model="domicilioForm.Observaciones"
                          label="Observaciones"
                          variant="outlined"
                          density="compact"
                          auto-grow
                          rows="1"
                          :clearable="true"
                          @keydown.enter="handleDomicilioEnterNavigation"
                          :rules="[
                            rules.maxLength(domicilioForm.Observaciones, 100)
                          ]"
                          :tabindex="isEditingDomicilio ? 14 : -1"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-form>
              </template>
              <template v-else>
                <VistaPreviaMedicamento
                  :datosVistaPrevia="{
                    pacienteApellidoNombre,
                    selectedFechaEmision,
                    pacienteEmail,
                    tipoDocValue,
                    pacienteDocumento,
                    pacienteCuil,
                    generoValue,
                    selectedFechaNacimiento,
                    financiadorValue,
                    planValue,
                    planSelected,
                    pacienteNumero,
                    pacienteTelefono,
                    validaAfiliado,
                    diagnostico,
                    medicamentosAgregados,
                    vistaPreviaHeadersMedicamentosAgregados,
                    hasMedicamentoRestringido: hasMedicamentoRestringido(),
                    domicilioPaciente,
                    domicilioPreviewDisplay
                  }"
                  :isMobile="isMobile"
                  :isVistaPrevia="isVistaPrevia"
                  @focusLastFieldOfCurrentStep="focusLastFieldOfCurrentStep"
                />
              </template>
            </v-card>
          </template>

          <!-- Step 4: Vista previa -->
          <template v-slot:[`item.4`]>
            <VistaPreviaMedicamento
              :datosVistaPrevia="{
                pacienteApellidoNombre,
                selectedFechaEmision,
                pacienteEmail,
                tipoDocValue,
                pacienteDocumento,
                pacienteCuil,
                generoValue,
                selectedFechaNacimiento,
                financiadorValue,
                planValue,
                planSelected,
                pacienteNumero,
                pacienteTelefono,
                validaAfiliado,
                diagnostico,
                medicamentosAgregados,
                vistaPreviaHeadersMedicamentosAgregados,
                hasMedicamentoRestringido: hasMedicamentoRestringido(),
                domicilioPaciente,
                domicilioPreviewDisplay
              }"
              :isMobile="isMobile"
              :isVistaPrevia="isVistaPrevia"
              @focusLastFieldOfCurrentStep="focusLastFieldOfCurrentStep"
            />
          </template>
        </v-stepper>
      </v-card-text>
      <v-card-actions>
        <v-row>
          <v-col cols="6" class="py-0">
            <v-btn
              @click="prevStep"
              color="red"
              id="atrasBtn"
              tabindex="99"
              :disabled="step == 1"
              >{{ previousText }}</v-btn
            >
          </v-col>
          <v-col cols="6" class="py-0">
            <v-btn
              class="to-right"
              @click="nextStep"
              id="siguienteBtn"
              color="primary"
              tabindex="97"
              :loading="loadingCreateRecipeBtn"
              :disabled="
                (step == 1 && (!afiliadoSearched || !isPacienteFormValid )) ||
                (step == 2 && (!isPrescripcionFormValid || medicamentosAgregados.length == 0 || diagnostico == null || diagnostico.trim().length == 0 || !matriculaSeleccionada || loadingMatriculas)) ||
                (isEditingDomicilio && !isDomicilioFormValid) ||
                (prestadorValidation && prestadorValidation.isBlocked)
              "
              >{{ nextText }}</v-btn
            >
          </v-col>
        </v-row>
      </v-card-actions>
      <!-- Modal Confirmar Domicilio -->
      <v-dialog v-model="showConfirmarDomicilio" :max-width="isMobile ? '95%' : '600px'" persistent>
        <ConfirmarDomicilioModal
          :paciente="{
            nroDoc: pacienteDocumento,
            cuil: pacienteCuil,
            tipoDoc: tipoDocSelected?.value1 || null,
            sexo: generoSelected?.id || null,
          }"
          :domicilioGecros="domicilioGecrosAfiliado"
          :auto-consultar-renaper="true"
          :auto-consultar-gecros="true"
          :tipoExpendioNombre="tipoExpendioRequiereDomicilio"
          :solicitudHash="solicitudId"
          :isMobile="isMobile"
          @accept="onConfirmarDomicilioAccept($event)"
          @modify="onConfirmarDomicilioModify($event)"
          @omit="onConfirmarDomicilioOmit($event)"
          @cancel="onConfirmarDomicilioCancel()"
        />
      </v-dialog>
      <v-dialog v-model="showAddWarningDialog" max-width="460">
        <v-card>
          <v-card-title class="text-subtitle-1">Advertencia</v-card-title>
          <v-card-text>
            <span
              v-html="
                messages.CONDICION_EXPENDIO_ADVERTENCIA.replace(
                  '{0}',
                  addWarningNombre
                )
              "
            ></span>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" color="grey" @click="cancelAddMedicamento"
              >Cancelar</v-btn
            >
            <v-btn color="primary" @click="confirmAddMedicamento"
              >Aceptar</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-if="dialogSendRecipe"
        v-model="dialogSendRecipe"
        :max-width="isMobile ? '95%' : '35%'"
        persistent
      >
        <SendRecipe
          :link="recipeLink"
          :email="pacienteEmail"
          :telefono="selectedPrefix + pacienteTelefono"
          :paciente="pacienteApellidoNombre"
          :solicitudId="solicitudId"
          tipoPrescripcion="medicamentos"
          @closeModal="closeModal($event)"
        />
      </v-dialog>
      <v-dialog
        v-if="dialogBusquedaMedicamentos"
        v-model="dialogBusquedaMedicamentos"
        :max-width="isMobile ? '95%' : '70%'"
        persistent
      >
        <BusquedaAvanzadaMedicamentos
          :financiadorId="financiadorSelected.id"
          :osId="
            validaAfiliado && validaCobertura ? selectedAfiliado.osId : null
          "
          :planId="
            validaAfiliado && validaCobertura ? selectedAfiliado.planId : null
          "
          :fecha="validaAfiliado && validaCobertura ? fechaEmision : null"
          :benId="
            validaAfiliado && validaCobertura ? selectedAfiliado.benId : null
          "
          @closeModalMedicamentos="closeModalMedicamentos()"
          @selectMedicamento="selectMedicamento($event)"
        />
      </v-dialog>
    </v-container>
  </v-card>
</template>

<script>
import enums from "@/utils/enums/index";
import DatePicker from "@/components/shared/DatePicker.vue";
import helperDate from "@/utils/helpers/dateTimePicker";
import { mapActions, mapGetters } from "vuex";
import rules from "@/utils/helpers/rules";
import SendRecipe from "@/components/modules/solicitudes/SendRecipe.vue";
import BusquedaAvanzadaMedicamentos from "@/components/shared/BusquedaAvanzadaMedicamentos.vue";
import { focusComponent } from "@/utils/helpers/htmlHelpers";
import CondicionExpendio from "@/components/shared/CondicionExpendio.vue";
import messages from "@/utils/enums/messages";
import cuilHelper from "@/utils/helpers/cuilHelper";
import ConfirmarDomicilioModal from "@/components/modules/solicitudes/ConfirmarDomicilioModal.vue";
import VistaPreviaMedicamento from "@/components/modules/solicitudes/VistaPreviaMedicamento.vue";

export default {
  name: "CreateRecetaElectronica",
  components: {
    DatePicker,
    SendRecipe,
    BusquedaAvanzadaMedicamentos,
    CondicionExpendio,
    ConfirmarDomicilioModal,
    VistaPreviaMedicamento,
  },
  props: {
    solicitudItem: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      messages,
      exigirDomicilio: false, // Controla si se exige domicilio para prescripciones
      domicilioModalShown: false, // Controla si ya se mostró el modal de domicilio para evitar repeticiones
      step: 1,
      rules: rules,
      steps: ["Datos del paciente", "Prescripción", "Vista previa"],
      completedSteps: [],
      closeIcon: enums.icons.CLOSE_ICON,
      addIcon: enums.icons.ADD_ICON,
      pacienteApellido: null,
      pacienteNombre: null,
      tiposDocItems: [],
      tipoDocSelected: null,
      pacienteDocumento: null,
      pacienteCuil: null,
      generatingCuil: false,
      generosItems: [],
      generoSelected: null,
      menuFechaEmision: false,
      fechaEmision: null,
      selectedFechaEmision: null,
      menuFechaNacimiento: false,
      fechaNacimiento: null,
      selectedFechaNacimiento: null,
      minDateFechaNacimiento: "1910",
      minDateFechaEmision: new Date().setHours(0, 0, 0, 0),
      minDateFechaEmisionFormatted: helperDate.convertDateFormat(
        helperDate.formatDate(new Date())
      ),

      maxDateFechaEmisionFormatted: null,
      maxDateFechaEmision: null,
      maxDateFechaNacimiento: null,
      maxDateFechaNacimientoFormatted: null,
      pacienteNumero: null,
      financiadoresItems: [],
      financiadorSelected: null,
      planesItems: [],
      planSelected: null,
      pacienteEmail: null,
      pacienteTelefono: null,
      previousText: "Volver",
      nextText: "Siguiente",
      prestadorApellidoNombre: null,
      provinciasItems: [],
      provinciaSelected: null,
      paisesItems: [],
      prestadorMatricula: null,
      matriculasDisponibles: [],
      matriculaSeleccionada: null,
      prestadorValidation: null,
      prestadorDataActual: null,
      selectedPrestador: null,
      prestadores: [],
      loadingPrestadores: false,
      selectedAfiliado: null,
      domicilioGecrosAfiliado: null,
      inputAfiliado: "",
      afiliados: [],
      loadingAfiliados: false,
      diagnostico: null,
      domicilioPrescripcion: null,
      selectedMedicamento: null,
      medicamentos: [],
      loadingMedicamentos: false,
      medicamentoCantidad: 1,
      posologia: null,
      medicamentosAgregados: [],
      headersMedicamentosAgregados: [
        { title: "Nombre", sortable: false, key: "regNom" },
        { title: "Cantidad", align: "end", sortable: false, key: "cantidad" },
        { title: "Posología", sortable: false, key: "posologia" },
        {
          title: "Condición expendio",
          sortable: false,
          key: "condicionExpendio",
          align: "center",
        },
        { title: "Acciones", align: "end", sortable: false, key: "actions" },
      ],
      vistaPreviaHeadersMedicamentosAgregados: [
        { title: "Nombre", sortable: false, key: "regNom" },
        { title: "Cantidad", sortable: false, key: "cantidad" },
        { title: "Posología", sortable: false, key: "posologia" },
      ],
      editIcon: enums.icons.EDIT_ICON,
      deleteIcon: enums.icons.DELETE_ICON,
      isPacienteFormValid: false,
      isPrescripcionFormValid: true,
      datosContacto: "",
      emailContacto: "",
      nombreConsultorio: "",
      direccionConsultorio: "",
      leyenda: "",
      imprimirDiagnostico: "",
      editMedicamentoAgregado: false,
      loadingCreateRecipeBtn: false,
      dialogSendRecipe: false,
      recipeLink: null,
      solicitudId: null,
      selectedPrefix: "+54",
      countryPrefixes: enums.enums.COUNTRYS_PREFIXES,
      afiliadoWithUploadedData: false,
      dialogBusquedaMedicamentos: false,
      searchIcon: enums.icons.SEARCH_ICON,
      validaAfiliado: null,
      validaCobertura: false,
      searchAfiliadoField: null,
      afiliadoSearched: false,
      isLoadingSearchAfiliado: false,
      alertMedicamentosDisplayed: false,
      showAddWarningDialog: false,
      pendingAddMedicamento: null,
      showConfirmarDomicilio: false,
      domicilioPaciente: null,
      domicilioForm: {
        Calle: null,
        Numero: null,
        Provincia: null,
        CPostal: null,
        DireccionCompleta: null,
        Observaciones: null,
        Barrio: null,
        Ciudad: null,
        Municipio: null,
        Pais: null,
        Monoblock: null,
        Piso: null,
        Departamento: null,
      },
      isDomicilioFormValid: false,
      loadingMatriculas: true,
    };
  },
  async created() {
    this.setDefaultDates();
    await this.setSelects();
    if (this.solicitudItem != null) this.setRecetaToCopy();
  },
  mounted() {},
  updated() {},
  beforeUnmount() {},
  watch: {
    selectedFechaEmision(val) {
      if (
        this.financiadorSelected !== null &&
        this.selectedAfiliado !== null &&
        val !== null &&
        val !== undefined
      ) {
        this.findAfiliado(); //volvemos a buscar el afiliado para verificar cobertura
      }
    },
    inputAfiliado(val) {
      if ((val?.trim() && val?.trim().length >= 3) || this.selectedAfiliado) {
        this.findAfiliado();
      } else {
        this.afiliados = [];
      }
    },
    tipoDocSelected() {
      // Limpiar CUIL si el tipo de documento es pasaporte
      if (this.tipoDocSelected?.value1?.toLowerCase() === 'pasa' ||
          this.tipoDocSelected?.value1?.toLowerCase() === 'pasaporte') {
        this.pacienteCuil = '';
      }

      // Revalidar el formulario al cambiar el tipo de documento
      this.$nextTick(() => {
        if (this.$refs["paciente-form"]) {
          this.$refs["paciente-form"].validate();
          setTimeout(() => {
            this.$refs["paciente-form"]?.validate?.();
          }, 50);
        }
      });
    },
    'domicilioForm.Pais'(newPais) {
      // Cuando cambia el país, resetear la provincia si no es Argentina
      // PERO solo si no hay una provincia ya asignada (evitar borrar datos importados de GECROS/RENAPER)
      let paisString = "";
      if (typeof newPais === "string") {
        paisString = newPais;
      } else if (newPais && typeof newPais === "object" && newPais.text) {
        paisString = newPais.text;
        // También actualizar el modelo para que sea consistente
        this.domicilioForm.Pais = newPais.text;
      }

      const paisNormalizado = (paisString || "").toString().toLowerCase().trim();
      // Solo resetear la provincia si no hay una ya asignada (evita borrar datos importados)
      if (paisNormalizado !== "argentina" && !this.domicilioForm.Provincia) {
        this.domicilioForm.Provincia = null;
        this.provinciaValue = null;
      }
    },
    step(newStep) {
      // Watcher centralizado para foco automático en todos los steps
      this.$nextTick(() => {
        switch (newStep) {
          case 1:
            // Focus en primer campo del paso 1 (Financiador)
            setTimeout(() => this.focusComponent("FinanciadorSelect", 50), 200);
            break;
          case 2:
            // Focus en primer campo del paso 2 (Diagnóstico)
            setTimeout(() => this.focusComponent("diagnosticoAutocomplete", 50), 200);
            break;
          case 3:
            if (this.hasMedicamentoRestringido()) {
              // Focus en primer campo del domicilio
              setTimeout(() => this.focusComponent("domicilio-calle", 50), 200);
            } else {
              // Focus en botón finalizar (vista previa) - delay mayor para esperar montaje
              setTimeout(() => this.focusComponent("siguienteBtn", 50), 350);
            }
            break;
          case 4:
            // Focus en botón finalizar (vista previa) - delay mayor para esperar montaje
            setTimeout(() => this.focusComponent("siguienteBtn", 50), 350);
            break;
        }
      });
    },
  },
  computed: {
    ...mapGetters({
      isMobile: "menu/isMobile",
      getExpendioNombrePorCodigo: "taxonomy/getExpendioNombrePorCodigo",
    }),
    provinciasFiltradas() {
      // Solo mostrar provincias si el país seleccionado es Argentina
      // Manejar tanto cuando Pais es un string (entrada manual) como cuando es un objeto (selección de lista)
      let paisSeleccionado = this.domicilioForm.Pais;

      if (typeof paisSeleccionado === 'object' && paisSeleccionado !== null) {
        // Si es un objeto, extraer el valor (preferir value, luego text)
        paisSeleccionado = paisSeleccionado.value || paisSeleccionado.text;
      }

      if (paisSeleccionado !== "Argentina") return [];

      // Transformar a Title Case con excepciones
      return this.provinciasItems.map(provincia => ({
        ...provincia,
        value: this.toTitleCase(provincia.value)
      }));
    },
    provinciaValue: {
      get() {
        return this.domicilioForm.Provincia;
      },
      set(value) {
        // Normalizar el valor para evitar "object object"
        this.domicilioForm.Provincia = this.normalizeProvinciaValue(value);
      }
    },
    stepsComputed() {
      const base = ["Datos del paciente", "Prescripción"];
      // Mostrar siempre el step "Editar domicilio" cuando exista al menos
      // un medicamento legalmente restringido (independiente de domicilioPaciente)
      if (this.hasMedicamentoRestringido()) base.push("Editar domicilio");
      base.push("Vista previa");
      return base;
    },
    isEditingDomicilio() {
      return this.step === 3 && this.hasMedicamentoRestringido();
    },
    pacienteApellidoNombre() {
      return `${this.pacienteApellido} ${this.pacienteNombre}`;
    },
    isDocumentoDNI() {
      // DNI => id = 1, value1 = 'DNI'
      return (
        this.tipoDocSelected &&
        (this.tipoDocSelected.id === 1 || this.tipoDocSelected?.value1 === "DNI")
      );
    },
    shouldMaskDocumento() {
      // Evitar pasar null a v-mask; decidir si se usa máscara con render condicional
      return this.isDocumentoDNI;
    },
    addWarningNombre() {
      // Mostrar el nombre del expendio para el diálogo de confirmación de agregar
      // Prioriza el nombre del ítem pendiente
      const nom = this.pendingAddMedicamento?.condicionExpendio;
      const cod = this.pendingAddMedicamento?.condicionExpendioCodigo;
      return (
        nom ||
        this.getExpendioNombrePorCodigo(cod)
      );
    },
    showGenerateCuil() {
      const docOk =
        this.pacienteDocumento != null &&
        this.pacienteDocumento.toString().trim() !== "";
      const sexoOk = this.generoSelected != null;
      const cuilVacio =
        !this.pacienteCuil || this.pacienteCuil.toString().trim() === "";
      return this.isDocumentoDNI && docOk && sexoOk && cuilVacio;
    },
    tipoExpendioRequiereDomicilio() {
      // Código(s) que requieren domicilio (string exacto, p.ej. '04')
      const CODIGOS_RESTRINGIDOS = ['04'];
      // Mostrar el nombre del primer código en la lista
      const codigo = CODIGOS_RESTRINGIDOS[0];
      return this.getExpendioNombrePorCodigo(codigo) || 'legalmente restringido';
    },
    domicilioPreviewDisplay() {
      const d = this.domicilioPaciente || {};
      if (d['DireccionCompleta']) return d['DireccionCompleta'];
      const partes = [];
      if (d.Calle) partes.push(d.Calle);
      if (d.Numero) partes.push(d.Numero);
      if (d.Piso) partes.push(d.Piso);
      if (d.Departamento) partes.push(d.Departamento);
      if (d.Monoblock) partes.push(d.Monoblock);
      if (d.Barrio) partes.push(d.Barrio);
      if (d.Municipio) partes.push(d.Municipio);
      if (d.Ciudad) partes.push(d.Ciudad);
      if (d.Provincia) partes.push(d.Provincia);
      if (d.Pais) partes.push(d.Pais);
      if (d.CPostal) partes.push(`CP: ${d.CPostal}`);
      if (d.Observaciones) partes.push(`Observaciones: ${d.Observaciones}`);
      return partes.length ? partes.join("; ") : "";
    },
    currentStepLabel() {
      return this.stepsComputed[this.step - 1] || null;
    },
    tipoDocValue() {
      return this.tipoDocSelected ? this.tipoDocSelected.value1 : '';
    },
    generoValue() {
      return this.generoSelected ? this.generoSelected.value : '';
    },
    financiadorValue() {
      return this.financiadorSelected ? this.financiadorSelected.value1 : '';
    },
    planValue() {
      return this.planSelected ? this.planSelected.value : '';
    },
    isVistaPrevia() {
      return this.step === 4 || (this.step === 3 && !this.hasMedicamentoRestringido());
    },
  },
  methods: {
    // Navegación por alias: devuelve índice 1-based del step con ese label, o -1
    getStepIndex(alias) {
      const idx = this.stepsComputed.indexOf(alias);
      return idx === -1 ? -1 : idx + 1;
    },
    // Limpiar campos del formulario de domicilio
    limpiarCamposDomicilio() {
      this.domicilioForm = {
        Calle: null,
        Numero: null,
        Provincia: null,
        CPostal: null,
        DireccionCompleta: null,
        Observaciones: null,
        Barrio: null,
        Ciudad: null,
        Municipio: null,
        Pais: null,
        Monoblock: null,
        Piso: null,
        Departamento: null,
      };
      // Limpiar también el domicilio paciente para consistencia
      this.domicilioPaciente = null;
    },
    // Limpiar campos y resetear validaciones del formulario de domicilio
    limpiarCamposDomicilioConValidacion() {
      this.limpiarCamposDomicilio();
      this.$nextTick(() => {
        if (this.$refs['domicilio-form']) {
          this.$refs['domicilio-form'].resetValidation();
          this.isDomicilioFormValid = true;
        }
      });
      this.focusComponent('domicilio-calle', 10);
    },
    handlePacienteDataChange() {
      // Limpiar domicilio y forzar mostrar modal nuevamente cuando cambian datos críticos del paciente
      this.limpiarCamposDomicilio();
      this.domicilioModalShown = false;

      // Autogenerar CUIL si es DNI y tenemos todos los datos necesarios
      this.$nextTick(() => {
        this.generarCuilAutomatico();
      });
    },
    ...mapActions({
      getFinanciadoresByUser: "taxonomy/getFinanciadoresByUser",
      getPlanesByFinanciador: "taxonomy/getPlanesByFinanciador",
      getTiposDocumentos: "taxonomy/getTiposDocumentos",
      getTiposDocumentoByFinanciador: "taxonomy/getTiposDocumentoByFinanciador",
      fetchPrestadoresByQuery: "taxonomy/fetchPrestadoresByQuery",
      getProvincias: "taxonomy/getProvincias",
      getMatriculas: "taxonomy/getMatriculas",
      getAfiliadoByNroAndFinanciador: "taxonomy/getAfiliadoByNroAndFinanciador",
      fetchAfiliadosFinanciadorByQuery:
        "taxonomy/fetchAfiliadosFinanciadorByQuery",
      createRecetaElectronica: "solicitud/createRecetaElectronica",
      getPrestadorDataByUserIdAndFinanciadorId:
        "solicitud/getPrestadorDataByUserIdAndFinanciadorId",
      getValidationsFinanciador: "financiador/getValidations",
      setAlert: "user/setAlert",
    }),
    focusComponent(refName, timeout) {
      focusComponent(refName, timeout);
    },
    // Método helper para construir la representación textual del domicilio
    buildDireccionCompletaFromObject(obj) {
      if (!obj) return null;
      if (obj.DireccionCompleta) return obj.DireccionCompleta;

      const partes = [];
      if (obj.Calle) partes.push(obj.Calle);
      if (obj.Numero) partes.push(obj.Numero);
      if (obj.Piso) partes.push(obj.Piso);
      if (obj.Departamento) partes.push(obj.Departamento);
      if (obj.Monoblock) partes.push(obj.Monoblock);
      if (obj.Barrio) partes.push(obj.Barrio);
      if (obj.Municipio) partes.push(obj.Municipio);
      if (obj.Ciudad) partes.push(obj.Ciudad);
      if (obj.Provincia) partes.push(obj.Provincia);
      if (obj.Pais) partes.push(obj.Pais);
      if (obj.CPostal) partes.push(`CP: ${obj.CPostal}`);
      if (obj.Observaciones) partes.push(`Observaciones: ${obj.Observaciones}`);
      return partes.length ? partes.join("; ") : null;
    },
    buildPacienteDomicilio() {
      // Si hay datos en el formulario de domicilio, enviarlos junto con DireccionCompleta
      const hasFormData = Object.values(this.domicilioForm || {}).some(v => v !== null && v !== undefined && (typeof v !== 'string' || v.trim() !== ''));
      if (hasFormData) {
        const DireccionCompleta = this.buildDireccionCompletaFromObject(this.domicilioForm);
        return { ...this.domicilioForm, 'DireccionCompleta': DireccionCompleta };
      }


      return null;
    },
    focusFirstFieldOfCurrentStep(event) {
      // Prevenir el comportamiento default del tab
      if (event.shiftKey) {
        return;
      }
      else {
      event.preventDefault();

      // Determinar el primer campo del step actual
      const currentLabel = this.stepsComputed[this.step - 1];
      let firstFieldId = "FinanciadorSelect";
      if (currentLabel === "Prescripción") firstFieldId = "diagnosticoAutocomplete";
      else if (this.isEditingDomicilio) firstFieldId = "importar-domicilio";
      else if (this.isVistaPrevia) firstFieldId = "fechaPrescripcionVistaPrevia";
      this.focusComponent(firstFieldId, 10);
    }}
    ,
    focusLastFieldOfCurrentStep(event) {
      if (event.shiftKey) {
        event.preventDefault();
        this.focusComponent('vBtnCloseCreateReceta', 10); // Botón cerrar = last field
      }
    },
    // Método helper común para navegación en formulario de domicilio
    navigateDomicilioFields() {
    // Definir el orden de navegación para los campos del formulario de domicilio
    const domicilioTabOrder = [
      'domicilio-calle',
      'domicilio-numero',
      'domicilio-piso',
      'domicilio-departamento',
      'domicilio-monoblock',
      'domicilio-barrio',
      'domicilio-ciudad',
      'domicilio-cpostal',
      'domicilio-municipio',
      'domicilio-pais',
      'domicilio-provincia',
      'domicilio-observaciones'
    ];

      // Obtener el elemento actualmente enfocado
      const activeElement = document.activeElement;
      const currentFieldId = activeElement ? activeElement.id : null;

      // Encontrar el índice del campo actual en el orden
      const currentIndex = currentFieldId ? domicilioTabOrder.indexOf(currentFieldId) : -1;

      if (currentIndex === -1) {
        // Si no se encuentra el campo actual, ir al primer campo
        this.focusComponent(domicilioTabOrder[0], 10);
        return;
      }

      // Si es el último campo, enfocar el botón "Siguiente"
      if (currentIndex === domicilioTabOrder.length - 1) {
        this.focusComponent('siguienteBtn', 10);
        return;
      }

      // Enfocar el siguiente campo en la secuencia
      const nextFieldId = domicilioTabOrder[currentIndex + 1];
      this.focusComponent(nextFieldId, 10);
    },
    handleDomicilioTabNavigation(event) {
      // Prevenir el comportamiento default del tab
      event.preventDefault();
      this.navigateDomicilioFields();
    },
    handleDomicilioEnterNavigation(event) {
      // Prevenir el comportamiento default del enter (que podría enviar formularios)
      event.preventDefault();
      this.navigateDomicilioFields();
    },
    onMatriculaChange(matricula) {
      if (matricula) {
        // Actualizar los datos del prestador con la matrícula seleccionada
        this.prestadorMatricula = matricula.matriculaProv.toString();
        this.matriculaSeleccionada = matricula.preXfinId;

        // Actualizar dirección de prescripción con la dirección de la matrícula seleccionada
        if (matricula.direccion && matricula.direccion.trim()) {
          this.domicilioPrescripcion = matricula.direccion;
        }

        // Actualizar los datos del prestador específicos de esta matrícula
        this.prestadorDataActual = {
          especialidad: matricula.preEspecialidad || null,
          entidadEmisora: matricula.entidadEmisoraMatricula || null,
          provincia: matricula.provinciaNombre || null,
          provinciaId: matricula.provinciaId || null,
          direccion: matricula.direccion || null,
          preXfinId: matricula.preXfinId,
        };
      }
  },
  // Método helper para normalizar valores de Provincia
  normalizeProvinciaValue(value) {
    if (value && typeof value === 'object' && value.value) {
      return value.value;
    }
    return value || null;
  },
  // Método para convertir a Title Case respetando "S/D" y corrigiendo CABA
  toTitleCase(text) {
    if (text === 'CABA') return 'Ciudad Autonoma de Buenos Aires';
    if (!text || text === 'S/D') return text;
    return text.toLowerCase().replace(/\b\w/g, (char) => {
      // Si es la primera letra de una palabra, convertir a mayúscula
      return char.toUpperCase();
    });
  },
  async setSelects() {
      const financiadores = await this.getFinanciadoresByUser();
      this.financiadoresItems = financiadores;
      if (this.financiadoresItems.length === 1) {
        this.financiadorSelected = financiadores[0];
        setTimeout(() => {
          const elementAfiliado = document.getElementById(
            "AfiliadoAutocomplete"
          );
          if (elementAfiliado) elementAfiliado.focus();
        }, 200);
        this.setDataFinanciador();
      } else {
        if (this.solicitudItem == null) {
          const selectComponent = document.getElementById("FinanciadorSelect");
          if (selectComponent) selectComponent.focus();
        }
      }
      this.generosItems = [
        { id: "M", value: "MASCULINO" },
        { id: "F", value: "FEMENINO" },
      ];
      const provincias = await this.getProvincias();
      this.provinciasItems = provincias;

      // Crear lista de países solo con nombres (sin códigos telefónicos)
      this.paisesItems = enums.enums.COUNTRYS_PREFIXES.map(pais => ({
        text: pais.text.replace(/\s*\([^)]*\)\s*$/, ''), // Remover código telefónico entre paréntesis
        value: pais.text.replace(/\s*\([^)]*\)\s*$/, '')  // Usar solo el nombre como valor
      }));
    },
    async loadTiposDoc() {
      const tiposDoc = await this.getTiposDocumentos();
      this.tiposDocItems = tiposDoc;
    },
    setDefaultDates() {
      const currentDate = new Date();
      this.fechaEmision = currentDate;
      this.selectedFechaEmision = this.formatDate(currentDate);
      this.fechaNacimiento = currentDate;
      this.selectedFechaNacimiento = this.formatDate(currentDate);
      this.maxDateFechaNacimientoFormatted = helperDate.convertDateFormat(
        this.formatDate(currentDate)
      );
    },
    async setRecetaToCopy() {
      this.financiadorSelected = this.financiadoresItems.find(
        (x) =>
          x.id == this.solicitudItem.receta.paciente.cobertura.nroFinanciador
      );
      await this.setDataFinanciador();

      if (this.validaAfiliado)
        this.inputAfiliado = this.solicitudItem.receta.paciente.nroDoc;
      else {
        this.afiliadoSearched = true;
        this.planSelected = this.solicitudItem.receta.paciente.cobertura.plan;
        this.pacienteApellido = this.solicitudItem.receta.paciente.apellido;
        this.pacienteNombre = this.solicitudItem.receta.paciente.nombre;
        this.tipoDocSelected = this.tiposDocItems.find(
          (x) => x.value1 == this.solicitudItem.receta.paciente.tipoDoc
        );
        this.pacienteDocumento = this.solicitudItem.receta.paciente.nroDoc;
        this.pacienteCuil = this.solicitudItem.receta.paciente.cuil || "";
        this.generoSelected = this.generosItems.find(
          (x) => x.id == this.solicitudItem.receta.paciente.sexo
        );
        this.pacienteNumero =
          this.solicitudItem.receta.paciente.cobertura.numero;
        this.fechaNacimiento = new Date(
          this.solicitudItem.receta.paciente.fechaNacimiento
        );
        this.selectedFechaNacimiento = this.formatDate(
          new Date(this.solicitudItem.receta.paciente.fechaNacimiento)
        );
        this.pacienteEmail = this.solicitudItem.receta.paciente.email;
        this.pacienteTelefono = this.solicitudItem.receta.paciente.telefono;
      }
      this.diagnostico = this.solicitudItem.receta.diagnostico;
      // Intentar preservar la matrícula original si está disponible
      const numeroMatriculaOriginal =
        this.solicitudItem.receta?.medico?.matricula?.numero ||
        this.solicitudItem.nroMatricula;
      if (numeroMatriculaOriginal && this.matriculasDisponibles.length > 1) {
        const matriculaOriginal = this.matriculasDisponibles.find(
          (m) =>
            m.matriculaProv?.toString() === numeroMatriculaOriginal?.toString()
        );
        if (matriculaOriginal) {
          this.matriculaSeleccionada = matriculaOriginal.preXfinId;
          this.onMatriculaChange(matriculaOriginal);
        }
      }
      this.afiliadoWithUploadedData = true;

      this.medicamentosAgregados =
        this.solicitudItem.receta.medicamentos.filter(
          (med) =>
            !this.solicitudItem.medicamentosBaja.some(
              (baja) => baja.regNo === med.regNo
            )
        );

      this.medicamentosAgregados.forEach((x) => {
        this.medicamentos.push({
          id: x.regNo,
          nombrePresentacion: x.regNom,
        });
      });
    },
    selectFechaEmision(event) {
      this.selectedFechaEmision = event.dateString;
      this.fechaEmision = event.date;
      if (this.financiadorSelected != null && this.selectedAfiliado != null) {
        this.afiliadoWithUploadedData = true;
        this.setAfiliado(this.selectedAfiliado);
      }
    },
    selectFechaNacimiento(event) {
      this.selectedFechaNacimiento = event.dateString;
      this.fechaNacimiento = event.date;
    },
    async setDataFinanciador() {
      // Limpiar datos de pasos posteriores al cambiar financiador
      this.clearStepsAfterOne();

      try {
        // Paralelizar operaciones independientes para mejorar rendimiento
        await Promise.all([
          this.loadValidationsFinanciador(),
          this.loadTiposDoc() // ✅ No depende de datos del prestador
        ]);

        // Cargar datos del prestador en background (sin bloquear UI crítica)
        const prestadorPromise = this.loadPrestadorData();

        // Cargar planes si es necesario (depende de validaAfiliado)
        let planesPromise = Promise.resolve();
        if (this.validaAfiliado) {
          planesPromise = this.loadPlanes();
        }

        // Esperar a que las operaciones independientes terminen
        await planesPromise;

        // Verificar si el prestador está bloqueado
        await prestadorPromise;
        if (this.prestadorValidation && this.prestadorValidation.isBlocked) {
          // Resetear validación para que no interfiera
          this.isPacienteFormValid = false;
          return;
        }
      } catch (error) {
        console.error(error);
      }
    },
    clearStepsAfterOne() {
      // Reset al paso 1
      this.step = 1;
      this.completedSteps = [];

      // Limpiar datos del paciente (paso 1 se mantiene parcialmente)
      this.afiliadoSearched = false;
      this.selectedAfiliado = null;
      this.domicilioGecrosAfiliado = null;
      this.inputAfiliado = "";
      this.searchAfiliadoField = null;
      this.afiliados = [];
      this.loadingAfiliados = false;
      this.isLoadingSearchAfiliado = false;

      // Limpiar datos personales del paciente (excepto fecha emisión)
      this.pacienteApellido = null;
      this.pacienteNombre = null;
      this.tipoDocSelected = null;
      this.pacienteDocumento = null;
      this.pacienteCuil = "";
      this.generoSelected = null;
      this.pacienteNumero = null;
      this.fechaNacimiento = null;
      this.selectedFechaNacimiento = null;
      this.pacienteEmail = null;
      this.pacienteTelefono = null;
      this.selectedPrefix = "+54";
      this.afiliadoWithUploadedData = false;

      // Limpiar planes (se recargarán con el nuevo financiador)
      this.planSelected = null;
      this.planesItems = [];

      // Limpiar datos de prescripción (paso 2)
      this.diagnostico = null;
      this.domicilioPrescripcion = null;
      this.selectedMedicamento = null;
      this.medicamentos = [];
      this.loadingMedicamentos = false;
      this.medicamentoCantidad = 1;
      this.posologia = null;
      this.medicamentosAgregados = [];
      this.editMedicamentoAgregado = false;
      this.alertMedicamentosDisplayed = false;
      this.showAddWarningDialog = false;
      this.pendingAddMedicamento = null;

      // Limpiar datos de domicilio (paso 3)
      this.limpiarCamposDomicilio();
      this.domicilioModalShown = false;
      this.showConfirmarDomicilio = false;

      // Reset de validaciones
      this.isPacienteFormValid = false;
      this.isPrescripcionFormValid = true;
      this.isDomicilioFormValid = false;

      // Reset de textos y estados
      this.previousText = "Volver";
      this.nextText = "Siguiente";
      this.loadingCreateRecipeBtn = false;

      // Reset de matrículas y prestador (se recargarán)
      this.loadingMatriculas = true;
      this.matriculaSeleccionada = null;
      this.prestadorValidation = null;
      this.prestadorDataActual = null;
      this.selectedPrestador = null;
      this.prestadores = [];
      this.loadingPrestadores = false;

      // Reset de validaciones del financiador
      this.validaAfiliado = null;
      this.validaCobertura = false;

      // Reset de modales
      this.dialogSendRecipe = false;
      this.dialogBusquedaMedicamentos = false;
      this.recipeLink = null;
      this.solicitudId = null;
    },
    async loadValidationsFinanciador() {
      if (this.financiadorSelected != null) {
        try {
          const validations = await this.getValidationsFinanciador(
            this.financiadorSelected?.id
          );
          // Valida afiliado
          this.validaAfiliado = validations.validaAfiliado;
          // Días emisión futuro
          let futureDate = new Date();
          futureDate.setDate(
            futureDate.getDate() + (validations.diasEmisionFuturo - 1)
          );
          this.maxDateFechaEmisionFormatted = helperDate.convertDateFormat(
            helperDate.formatDate(futureDate)
          );
          this.maxDateFechaEmision = futureDate.setHours(0, 0, 0, 0);
          // Valida cobertura
          this.validaCobertura = validations.validaCobertura;
        } catch (error) {
          console.error(error);
        }
      }
    },
    async loadPrestadorData() {
      if (this.financiadorSelected != null) {
        try {
          this.prestadorValidation =
            await this.getPrestadorDataByUserIdAndFinanciadorId(
              this.financiadorSelected?.id
            );

          // Verificar si está bloqueado (caso tradicional)
          if (this.prestadorValidation.isBlocked) {
            // No mostrar setAlert porque ahora tenemos una alerta inline
            return; // No configurar datos si está bloqueado
          }

          // Verificar si tenemos datos válidos del prestador (no vacío por validación SISA fallida)
          const tieneDatosValidos = this.prestadorValidation.prestadorData &&
                                   this.prestadorValidation.prestadorData.matp &&
                                   this.prestadorValidation.prestadorData.nombre;

          if (tieneDatosValidos) {
            // Configurar datos del prestador solo si son válidos
            this.prestadorMatricula =
              this.prestadorValidation.prestadorData.matp.toString();
            this.prestadorApellidoNombre =
              this.prestadorValidation.prestadorData.nombre;
            this.prestadorData = this.prestadorValidation.prestadorData;

            // Configurar matrículas disponibles
            this.matriculasDisponibles =
              this.prestadorValidation.matriculasDisponibles || [];

            // Buscar la matrícula por defecto (con esDefault = true) o la primera si no hay ninguna marcada
            const matriculaPorDefecto =
              this.matriculasDisponibles.find((m) => m.esDefault) ||
              this.matriculasDisponibles[0];

            // Configurar la matrícula seleccionada como la matrícula por defecto
            this.matriculaSeleccionada = matriculaPorDefecto?.preXfinId;

            // Configurar dirección de prescripción con la dirección de la matrícula por defecto si está disponible
            if (
              matriculaPorDefecto?.direccion &&
              matriculaPorDefecto.direccion.trim()
            ) {
              this.domicilioPrescripcion = matriculaPorDefecto.direccion;
            }

            // Configurar datos del prestador con la matrícula por defecto
            if (matriculaPorDefecto) {
              this.prestadorDataActual = {
                especialidad: matriculaPorDefecto.preEspecialidad || null,
                entidadEmisora:
                  matriculaPorDefecto.entidadEmisoraMatricula || null,
                provincia: matriculaPorDefecto.provinciaNombre || null,
                provinciaId: matriculaPorDefecto.provinciaId || null,
                direccion: matriculaPorDefecto.direccion || null,
                preXfinId: matriculaPorDefecto.preXfinId,
              };
            }
          } else {
            // No hay datos válidos del prestador (probablemente validación SISA fallida)
            // Configurar valores por defecto para continuar con validación local
            this.matriculasDisponibles = [];
            this.matriculaSeleccionada = null;
            this.prestadorDataActual = null;
          }

          // Mostrar warnings si los hay
          if (
            this.prestadorValidation.warnings &&
            this.prestadorValidation.warnings.length > 0
          ) {
            this.prestadorValidation.warnings.forEach((warning) => {
              this.setAlert({
                type: "warning",
                message: warning,
                timeout: 8000,
              });
            });
          }
        } catch (error) {
          console.error("Error al obtener datos del prestador:", error);
          this.setAlert({
            type: "error",
            message:
              "Error al validar los datos del prestador.",
            timeout: 5000,
          });
        }
      }
      this.loadingMatriculas = false;
    },
    async loadPlanes() {
      if (this.financiadorSelected != null) {
        const planes = await this.getPlanesByFinanciador({
          financiadorId: this.financiadorSelected?.id,
          fecha: this.selectedFechaEmision,
        });
        this.planesItems = planes;
      } else {
        this.planesItems = [];
        this.planSelected = null;
      }
    },
    selectMedicamento(medicamento) {
      this.medicamentos.push(medicamento);
      this.selectedMedicamento = medicamento;
      this.dialogBusquedaMedicamentos = false;
      focusComponent("cantidadMed", 100);
    },
    async findAfiliado() {
      if (this.timerAfiliado) {
        clearTimeout(this.timerAfiliado);
        this.timerAfiliado = null;
      }
      this.timerAfiliado = setTimeout(async () => {
        if (this.afiliados?.some((x) => x.title == this.inputAfiliado)) {
          return;
        } else if (!this.afiliadoWithUploadedData) {
          this.loadingAfiliados = true;
          const response = await this.fetchAfiliadosFinanciadorByQuery({
            fecha: this.selectedFechaEmision,
            input: this.inputAfiliado.toString().trim(),
            financiadorId: this.financiadorSelected?.id,
          });
          this.$refs["afiliados-autocomplete"].cachedItems = [];
          this.afiliados = response;
          this.loadingAfiliados = false;
          if (this.selectedAfiliado) {
            this.selectedAfiliado = response[0];
            this.setsAlertsAfiliado(this.selectedAfiliado);
          }
        } else {
          this.loadingAfiliados = true;
          const response = await this.fetchAfiliadosFinanciadorByQuery({
            fecha: this.selectedFechaEmision,
            input: this.inputAfiliado.toString().trim(),
            financiadorId: this.financiadorSelected?.id,
          });
          this.$refs["afiliados-autocomplete"].cachedItems = [];
          this.afiliados = response;
          this.setAfiliado(response[0]);
          this.loadingAfiliados = this.afiliadoWithUploadedData = false;
        }
      }, 1000);
    },
    setsAlertsAfiliado(selectedAfiliado) {
      switch (selectedAfiliado.estadoBenef.id) {
        case 1:
          this.setAlert({
            type: "success",
            message: "El afiliado posee cobertura.",
          });
          break;
        case 2:
          this.setAlert({
            type: "warning",
            message: "El afiliado se encuentra sin cobertura por baja.",
          });
          break;
        case 3:
          this.setAlert({
            type: "warning",
            message: "El afiliado se encuentra momentaneamente sin cobertura.",
          });
          break;
        case 4:
          this.setAlert({
            type: "warning",
            message:
              "El afiliado se encuentra momentaneamente sin cobertura, en gestión judicial.",
          });
          break;
        default:
          this.setAlert({
            type: "warning",
            message: "El afiliado se encuentra sin cobertura.",
          });
          break;
      }
    },
    setAfiliado(afiliado) {
      this.afiliadoSearched = true;
      if (this.afiliadoWithUploadedData) this.selectedAfiliado = afiliado;
      else this.selectedAfiliado = afiliado.raw;
      this.domicilioGecrosAfiliado = this.selectedAfiliado.domicilioGecros;
      this.setsAlertsAfiliado(this.selectedAfiliado);

      this.planSelected = this.planesItems.find(
        (x) => x.id == this.selectedAfiliado.planId
      );
      this.pacienteApellido = this.selectedAfiliado.benApe;
      this.pacienteNombre = this.selectedAfiliado.benNom;
      this.tipoDocSelected = this.tiposDocItems.find(
        (x) => x.id == this.selectedAfiliado.tipoDocId
      );
      this.pacienteDocumento = this.selectedAfiliado.docId;
      this.pacienteCuil = this.selectedAfiliado.cuil || ""; // Asegurar que sea string vacío si no viene
      this.generoSelected = this.generosItems.find(
        (x) => x.id == this.selectedAfiliado.genero
      );
      this.pacienteNumero = this.selectedAfiliado.nroAfiliado;
      this.pacienteEmail = this.selectedAfiliado.email;
      this.pacienteTelefono = this.selectedAfiliado.telefono;

      this.selectedFechaNacimiento = this.selectedAfiliado.fechaNacimiento;
      const partesFecha = this.selectedFechaNacimiento.split("/");
      const fecha = new Date(
        partesFecha[2],
        partesFecha[1] - 1,
        partesFecha[0]
      );
      this.fechaNacimiento = fecha;

      this.inputAfiliado = this.selectedAfiliado.docId.toString();

      // Esperar a que Vue actualice completamente el DOM antes de validar y cambiar foco
      // para evitar problemas de animación con scale(NaN)
      this.$nextTick(() => {
        // Validar el formulario después de que el DOM se haya actualizado
        if (this.$refs["paciente-form"]) {
          this.$refs["paciente-form"].validate();
        }

        // Cambiar foco después de un breve delay adicional para asegurar estabilidad
        this.$nextTick(() => {
          this.seleccionarSiguiente();
        });
      });
    },
    async searchAfiliado() {
      this.isLoadingSearchAfiliado = true;
      const data = {
        numero: this.searchAfiliadoField?.trim(),
        financiadorId: this.financiadorSelected?.id,
      };

      // Construir domicilio del paciente reutilizando la vista previa o campos estructurados
      // Asegurarnos de inicializar data.paciente antes de asignar propiedades
      data.paciente = data.paciente || {};
      data.paciente.domicilio = this.buildPacienteDomicilio();
      try {
        const response = await this.getAfiliadoByNroAndFinanciador(data);

        // Esperar a que Vue actualice completamente el DOM antes de asignar valores y validar
        // para evitar problemas de animación con scale(NaN)
        this.$nextTick(() => {
          if (response != null) {
            this.pacienteApellido = response.benApe;
            this.pacienteNombre = response.benNom;
            this.pacienteDocumento = response.docId;
            this.pacienteCuil = response.cuil || ""; // Asegurar que sea string vacío si no viene
            this.pacienteEmail = response.email;
            this.fechaNacimiento = new Date(
              Date.parse(response.fechaNacimiento + "T00:00:00")
            );
            this.selectedFechaNacimiento = this.formatDate(this.fechaNacimiento);
            this.generoSelected = this.generosItems.find(
              (x) => x.id == response.genero
            );
            this.pacienteNumero = response.nroAfiliado;
            this.planSelected = response.plan;
            this.pacienteTelefono = response.telefono;
            this.tipoDocSelected = this.tiposDocItems.find(
              (x) => x.value1 == response.tipoDoc
            );

            this.setAlert({
              type: "success",
              message:
                "El afiliado ha sido encontrado en el sistema. A continuación se cargarán sus respectivos datos, por favor verificar los mismos.",
            });
          } else {
            this.setAlert({
              type: "info",
              message:
                "El afiliado no ha sido encontrado en el sistema. Cargue sus datos para continuar con la prescripción.",
            });
          }
          this.afiliadoSearched = true;

          // Validar el formulario después de que el DOM se haya actualizado completamente
          this.$nextTick(() => {
            if (this.$refs["paciente-form"]) {
              this.$refs["paciente-form"].validate();
            }
            // Hacer foco en el select de financiador después de buscar afiliado
            this.focusComponent("FinanciadorSelect", 10);
          });
        });
      } catch (error) {
        console.error(error);
      }
      this.isLoadingSearchAfiliado = false;
    },
    seleccionarSiguiente() {
      setTimeout(() => {
        let blurHelper = document.getElementById("blurHelper");
        blurHelper.focus();
        let componente = document.getElementById("siguienteBtn");
        componente.focus();
      }, 200);
    },
    customFilterAfiliado(item) {
      return item.nroAfiliado || item.benNombreCompleto || item.docId;
    },
    addMedicamento() {
      if (this.medicamentosAgregados.length === 3) {
        this.setAlert({
          type: "warning",
          message:
            "No es posible agregar más de 3(tres) medicamentos en una misma receta.",
        });
        return;
      }
      if (
        this.medicamentosAgregados.some(
          (x) => x.regNo == this.selectedMedicamento.id
        )
      ) {
        this.setAlert({
          type: "warning",
          message: "No es posible agregar el mismo medicamento dos veces.",
        });
        return;
      }

      // Si el medicamento seleccionado viene con advertencia de condición de expendio, mostrar confirmación
      if (this.selectedMedicamento?.condicionExpendioAdvertencia) {
        this.pendingAddMedicamento = null;
        this.pendingAddMedicamento = this.selectedMedicamento;
        this.showAddWarningDialog = true;
        return;
      }

      this.doAddMedicamento();
    },
    confirmAddMedicamento() {
      this.showAddWarningDialog = false;
      if (this.pendingAddMedicamento) {
        // Asegurar que el seleccionado sigue siendo el mismo
        this.selectedMedicamento = this.pendingAddMedicamento;
        this.doAddMedicamento();
      }
    },
    cancelAddMedicamento() {
      // Cierra el diálogo y NO agrega el medicamento
      this.showAddWarningDialog = false;
    },
    generarCUIL() {
      this.generatingCuil = true;
      try {
        const sexo = this.generoSelected?.id;
        const nroDoc = (this.pacienteDocumento || "")
          .toString()
          .replaceAll(".", "");
        if (!this.isDocumentoDNI || !sexo || !nroDoc) {
          this.generatingCuil = false;
          return;
        }

        // Simular operación asíncrona con timeout de 1 segundo
        setTimeout(() => {
          try {
            const cuil = cuilHelper.generarCuil(sexo, nroDoc);
            const formatted = `${cuil.substring(0, 2)}-${cuil.substring(2, 10)}-${cuil.substring(10)}`;
            this.pacienteCuil = formatted;
            this.$nextTick(() => this.$refs["paciente-form"]?.validate?.());
          } catch (error) {
            console.error("Error generando CUIL:", error);
          } finally {
            this.generatingCuil = false;
          }
        }, 1000);
      } catch (error) {
        console.error("Error generando CUIL:", error);
        this.generatingCuil = false;
      }
    },
    generarCuilAutomatico() {
      if (!this.isDocumentoDNI) return;

      const documento = this.pacienteDocumento?.toString().replace(/\D/g, '');
      const sexo = this.generoSelected?.id;

      if (!documento || !sexo) return;

      try {
        const cuilGenerado = cuilHelper.generarCuil(sexo, documento);
        const cuilFormateado = `${cuilGenerado.substring(0, 2)}-${cuilGenerado.substring(2, 10)}-${cuilGenerado.substring(10)}`;

        this.pacienteCuil = cuilFormateado;
      } catch (error) {
        console.error("Error generando CUIL automáticamente:", error);
      }
      
    },
    doAddMedicamento() {
      this.medicamentosAgregados.push({
        regNo: this.selectedMedicamento.id,
        regNom: this.selectedMedicamento.nombrePresentacion,
        cantidad: Math.trunc(this.medicamentoCantidad),
        posologia: this.posologia,
        condicionExpendio: this.selectedMedicamento?.condicionExpendio ?? null,
        condicionExpendioCodigo:
          this.selectedMedicamento?.condicionExpendioCodigo ?? null,
        condicionExpendioAdvertencia:
          this.selectedMedicamento?.condicionExpendioAdvertencia || false,
      });
      this.posologia = this.selectedMedicamento = null;
      this.editMedicamentoAgregado = false;
      this.domicilioModalShown = false;

      // Foco automático al botón buscar medicamentos para flujo continuo
      this.$nextTick(() => {
        this.focusComponent('buscarMedBtn', 100);
      });
    },
    editMedicamento(item) {
      this.editMedicamentoAgregado = true;
      this.medicamentosAgregados = this.medicamentosAgregados.filter(
        (x) => x.regNo !== item.regNo
      );
      this.medicamentoCantidad = item.cantidad;
      this.selectedMedicamento = this.medicamentos.find(
        (x) => x.id == item.regNo
      );
      this.posologia = item.posologia;
      // Preservar condición de expendio si estaba presente
      if (this.selectedMedicamento) {
        this.selectedMedicamento.condicionExpendio = item.condicionExpendio;
        this.selectedMedicamento.condicionExpendioAdvertencia =
          item.condicionExpendioAdvertencia;
      }
      this.focusComponent("buscarMedBtn", 10);
    },
    deleteMedicamento(item) {
      this.medicamentosAgregados = this.medicamentosAgregados.filter(
        (x) => x.regNo !== item.regNo
      );
      this.focusComponent("buscarMedBtn", 10);
      this.domicilioModalShown = false;
    },
    formatDate(date) {
      return helperDate.formatDate(date);
    },
    hasMedicamentoRestringido() {
      const CODIGOS_RESTRINGIDOS = ["04"];
      return (this.medicamentosAgregados || []).some((m) => {
        const cod = (m?.condicionExpendioCodigo ?? m?.condicionExpendio ?? "").toString().trim();
        return CODIGOS_RESTRINGIDOS.includes(cod);
      });
    },
    fillDomicilioForm(domicilio) {
      if (domicilio) {
        // IMPORTANTE: Los datos de GECROS/RENAPER tienen prioridad absoluta
        // Se asignan tal cual llegan del modal, incluyendo todos los campos disponibles
        Object.keys(domicilio).forEach(key => {
          if (domicilio[key] !== undefined) {
            this.domicilioForm[key] = domicilio[key];
          }
        });

        // Si viene paisId = 1 desde GECROS, forzar el campo Pais como "Argentina"
        if (this.selectedAfiliado?.domicilioGecros?.paisId === 1) {
          this.domicilioForm.Pais = "Argentina";
        }
      }
    },
    onConfirmarDomicilioAccept(domicilio) {
      this.showConfirmarDomicilio = false;
      this.domicilioPaciente = domicilio || null;

      // Llenar los campos del formulario del paso 3
      this.fillDomicilioForm(domicilio);

      // saltar a Vista previa directamente (usar índice dinámico)
      const previewStep = Math.max(1, this.getStepIndex('Vista previa'));
      this.step = Math.min(previewStep, this.stepsComputed.length);
      this.nextText = this.step === this.stepsComputed.length ? 'Finalizar' : this.nextText;
    },
    onConfirmarDomicilioModify(domicilio) {
      this.showConfirmarDomicilio = false;
      this.fillDomicilioForm(domicilio);

      // Esperar al siguiente tick para asegurarnos que `stepsComputed` esté actualizado
      this.$nextTick(() => {
        const editIndex = this.getStepIndex('Editar domicilio');
        if (editIndex !== -1) {
          this.step = editIndex;
        } else {
          // Fallback coherente: preferir volver a 'Prescripción' si existe
          const prescIndex = this.getStepIndex('Prescripción');
          if (prescIndex !== -1) {
            this.step = prescIndex;
          } else {
            const previewIndex = this.getStepIndex('Vista previa');
            this.step = Math.max(1, previewIndex);
          }
        }

        this.$nextTick(() => this.focusComponent('domicilio-calle', 200));
      });
    },
    onConfirmarDomicilioCancel() {
      this.showConfirmarDomicilio = false;
      this.domicilioModalShown = false;
    },
    onConfirmarDomicilioOmit(domicilio) { // eslint-disable-line no-unused-vars
      this.showConfirmarDomicilio = false;
      // Tomar los datos del formulario de editar domicilio, como estén
      this.domicilioPaciente = this.buildPacienteDomicilio();
      // saltar a Vista previa directamente (usar índice dinámico)
      const previewStep = Math.max(1, this.getStepIndex('Vista previa'));
      this.step = Math.min(previewStep, this.stepsComputed.length);
      this.nextText = this.step === this.stepsComputed.length ? 'Finalizar' : this.nextText;
    },
    nextStep() {
      // Interceptar avance desde Prescripción para condición de expendio restringido (lista)
      if (this.step === 2) {
        if (this.hasMedicamentoRestringido()) {
          // Solo mostrar el modal si no se ha mostrado antes en esta sesión
          if (!this.domicilioModalShown) {
            this.showConfirmarDomicilio = true;
            this.domicilioModalShown = true;
            return;
          }
          // Si ya se mostró el modal antes, continuar normalmente
        }
      }
      // Al avanzar desde Editar domicilio, permitir avanzar si TODOS los campos
      // del formulario de domicilio están vacíos (asignando null), o validar
      // y asignar el objeto cuando haya contenido.
      if (this.isEditingDomicilio) {
        const isDomicilioFormVacio = (d) => {
          if (!d) return true;
          const valores = Object.values(d || {});
          return !valores.some(v => v !== null && v !== undefined && (typeof v !== 'string' || v.trim() !== ''));
        };

        if (isDomicilioFormVacio(this.domicilioForm)) {
          // No hay datos: enviar explicitamente null
          this.domicilioPaciente = null;
        } else {
          // Hay al menos un campo: requerir que el form sea válido antes de asignar
          if (!this.isDomicilioFormValid) return;
          this.domicilioPaciente = { ...this.domicilioForm };
        }
      }
      if (this.step < this.stepsComputed.length) {
        this.step++;
        switch (this.step) {
          case 2:
            if (this.solicitudItem?.medicamentosBaja.length ?? 0 > 0) {
              if (!this.alertMedicamentosDisplayed) {
                this.setAlert({
                  type: "warning",
                  message: `Se eliminará/n el/los medicamento/s: '${this.solicitudItem.medicamentosBaja
                    .map((mb) => mb.regNom)
                    .join(", ")}' ya que se encuentra/n dado/s de baja.`,
                  timeout: 10000,
                });
              }
              this.alertMedicamentosDisplayed = true;
            }

            if (
              this.diagnostico != null &&
              this.domicilioPrescripcion != null &&
              this.medicamentosAgregados.length > 0
            )
              this.isPrescripcionFormValid = true;
            else this.isPrescripcionFormValid = false;
            //vuelvo a setear la fecha porque se ponia undefined
            this.selectedFechaNacimiento = this.formatDate(
              this.fechaNacimiento
            );
            break;
          case 3:
            break;
          case 4:
            break;
          default:
            break;
        }
        if (this.step === this.stepsComputed.length) {
          this.nextText = "Finalizar";
        }
      } else this.createRecipe();
    },
    prevStep() {
      this.nextText = "Siguiente";
      if (this.step > 0) {
        // Si estamos regresando desde el step 3 (Editar domicilio) con medicamentos restringidos,
        // preservar los datos escritos en el formulario
        if (this.step === 3 && this.hasMedicamentoRestringido()) {
          this.domicilioPaciente = this.buildPacienteDomicilio();
        }

        this.step--;
        switch (this.step) {
          case 1:
            this.isPrescripcionFormValid = true;
            break;
          default:
            break;
        }
        // Focus automático al volver a un step anterior
        this.$nextTick(() => {
          switch (this.step) {
            case 1:
              // Focus en el primer campo del step 1 (Financiador)
              this.focusComponent("FinanciadorSelect", 200);
              break;
            case 2:
              // Focus en el primer campo del step 2 (Diagnóstico)
              this.focusComponent("diagnosticoAutocomplete", 200);
              break;
            case 3:
              // Focus en el primer campo del domicilio cuando hay medicamentos restringidos
              if (this.hasMedicamentoRestringido()) {
                this.focusComponent("domicilio-calle", 200);
              }
              break;
            default:
              break;
          }
        });
      }
    },
    closeModal(reload) {
      this.$emit("closeModal", reload);
    },
    closeModalMedicamentos() {
      this.dialogBusquedaMedicamentos = false;
    },
    openModalMedicamentos() {
      this.dialogBusquedaMedicamentos = true;
    },
    async createRecipe() {
      this.loadingCreateRecipeBtn = true;
      // Validación condicional de domicilio según configuración
      if (this.exigirDomicilio && this.hasMedicamentoRestringido() && !this.domicilioPaciente) {
        this.setAlert({ type: 'warning', message: 'Se requiere confirmar/ingresar el domicilio del paciente para medicamentos legalmente restringidos.' });
        this.loadingCreateRecipeBtn = false;
        return;
      }
      
      // Obtener datos de la matrícula seleccionada
      const matriculaSeleccionadaData = this.matriculasDisponibles.find(
        m => m.preXfinId === this.matriculaSeleccionada
      ) || this.matriculasDisponibles[0];
      
      const data = {
        medicamentos: this.medicamentosAgregados,
        paciente: {
          apellido: this.pacienteApellido,
          nombre: this.pacienteNombre,
          apellidoNombre: this.pacienteApellidoNombre,
          tipoDoc: this.tipoDocSelected?.value1,
          nroDoc:
            this.isDocumentoDNI
              ? (this.pacienteDocumento || "")
                  .toString()
                  .replaceAll(".", "")
              : this.pacienteDocumento.toString(),
          cuil:
            this.pacienteCuil != null &&
            this.pacienteCuil.toString().trim() !== ""
              ? this.pacienteCuil.toString().replaceAll("-", "")
              : this.pacienteCuil,
          sexo: this.generoSelected.id,
          fechaNacimiento: this.fechaNacimiento,
          localidad: "",
          provincia: "",
          email: this.pacienteEmail,
          telefono: this.pacienteTelefono,
          pais: "",
          cobertura: {
            nroFinanciador: this.financiadorSelected.id.toString(),
            plan: this.validaAfiliado
              ? this.planSelected?.value
              : this.planSelected,
            numero: this.pacienteNumero,
            privado: true,
          },
        },
        medico: {
          matricula: {
            tipo: "mp",
            numero: matriculaSeleccionadaData?.matriculaProv?.toString() || this.prestadorMatricula,
            provincia: matriculaSeleccionadaData?.provinciaNombre || "",
          },
          apellido: this.prestadorApellidoNombre,
          nombre: "",
          especialidad: matriculaSeleccionadaData?.preEspecialidad || "",
          preXfinId: this.matriculaSeleccionada,
        },
        diagnostico: this.diagnostico,
        domicilioPrescripcion: this.domicilioPrescripcion,
        horario: "",
        diasAtencion: "",
        datosContacto: this.datosContacto,
        nombreConsultorio: this.nombreConsultorio,
        direccionConsultorio: this.direccionConsultorio,
        email: this.emailContacto,
        informacionAdicional: "",
        clienteAppId: null,
        imprimirDiagnostico: "S",
        fechaEmision: this.fechaEmision,
      };
      // Enviar tanto DireccionCompleta como campos desglosados (algunos pueden ser null)
      const domicilioPacienteObj = this.buildPacienteDomicilio();
      // Garantizar que los campos desglosados existan aunque sean null
      if (domicilioPacienteObj) {
        data.paciente.domicilio = {
          direccionCompleta: domicilioPacienteObj['DireccionCompleta'] || null,
          calle: domicilioPacienteObj['Calle'] !== undefined ? domicilioPacienteObj['Calle'] : null,
          numero: domicilioPacienteObj['Numero'] !== undefined ? domicilioPacienteObj['Numero'] : null,
          piso: domicilioPacienteObj['Piso'] !== undefined ? domicilioPacienteObj['Piso'] : null,
          departamento: domicilioPacienteObj['Departamento'] !== undefined ? domicilioPacienteObj['Departamento'] : null,
          monoblock: domicilioPacienteObj['Monoblock'] !== undefined ? domicilioPacienteObj['Monoblock'] : null,
          barrio: domicilioPacienteObj['Barrio'] !== undefined ? domicilioPacienteObj['Barrio'] : null,
          municipio: domicilioPacienteObj['Municipio'] !== undefined ? domicilioPacienteObj['Municipio'] : null,
          ciudad: domicilioPacienteObj['Ciudad'] !== undefined ? domicilioPacienteObj['Ciudad'] : null,
          provincia: domicilioPacienteObj['Provincia'] !== undefined ? domicilioPacienteObj['Provincia'] : null,
          cpostal: domicilioPacienteObj['CPostal'] !== undefined ? domicilioPacienteObj['CPostal'] : (domicilioPacienteObj['CodigoPostal'] !== undefined ? domicilioPacienteObj['CodigoPostal'] : null),
          pais: domicilioPacienteObj['Pais'] !== undefined ? domicilioPacienteObj['Pais'] : null,
          observaciones: domicilioPacienteObj['Observaciones'] !== undefined ? domicilioPacienteObj['Observaciones'] : null,
        };
      } else {
        // enviar explícitamente null si no hay ningún dato
        data.paciente.domicilio = {
          direccionCompleta: null,
          calle: null,
          numero: null,
          piso: null,
          departamento: null,
          monoblock: null,
          barrio: null,
          municipio: null,
          ciudad: null,
          provincia: null,
          cpostal: null,
          pais: null,
          observaciones: null,
        };
      }
      try {
        const response = await this.createRecetaElectronica(data);
        if (response.status === 200) {
          this.setAlert({
            type: "success",
            message: "Receta creada con éxito.",
          });
          this.recipeLink = response.data.link;
          this.solicitudId = response.data.numero;
          this.dialogSendRecipe = true;
        }
      } catch (error) {
        console.error(error);
      }
      this.loadingCreateRecipeBtn = false;
    },
  },
};
</script>

<style scoped>
.center-input >>> input {
  text-align: center;
}
</style>
